import { createContext, useContext, useMemo, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { ApiClients } from '@/api/client';

// R6 (RBAC) — frontend-side capability gating.
//
// Backed by /v1/users/me/capabilities (R1). The provider hits the
// endpoint once at mount; the rest of the app reads via
// useCapability(cap) or wraps gated UI in <Gated capability="…">.
//
// Default behavior — until the endpoint resolves we render the gated
// content. Reason: the gating system shouldn't make existing buttons
// disappear on every page load while data is in flight. Once the
// payload arrives, the gate flips to its real value. This trades a
// small "render then maybe hide" flicker for a faster-feel app. Apps
// that prefer the inverse (don't render until known) can pass
// `defaultAllow={false}` to the provider.

export type Capability =
  | 'dashboard.view'
  | 'detail.view'
  | 'analyze.signal'
  | 'alert.acknowledge'
  | 'alert.assign'
  | 'alert.snooze'
  | 'alert.resolve'
  | 'alert.attach_session'
  | 'session.save_public'
  | 'sensor.pair'
  | 'sensor.edit_thresholds'
  | 'sensor.capture_photo'
  | 'sensor.commission'
  | 'asset.add'
  | 'asset.edit'
  | 'asset.remove'
  | 'asset.assign_sensor'
  | 'user.invite'
  | 'user.change_role'
  | 'user.deactivate'
  | 'group.manage'
  | 'user.promote_admin'
  | 'org.create'
  | 'org.edit'
  | 'org.edit_policy'
  | 'org.integrations'
  | 'org.api_keys'
  | 'org.audit_log'
  | 'org.delete'
  | 'radiant.manage_tenants'
  | 'radiant.impersonate_read'
  | 'radiant.impersonate_write';

export type CapabilitiesApi = {
  /** Bearer's resolved capability set. Empty array while loading. */
  capabilities: Set<Capability>;
  /** True before the endpoint has resolved (or when the call errors). */
  loading: boolean;
  /** Raw role from the JWT (technician/engineer/admin/viewer/...). */
  role: string | null;
  /** ADR-0010 role after legacy normalization. */
  normalizedRole: string | null;
  /** Convenience predicate. */
  has(c: Capability): boolean;
  /** Default-allow flag exposed so <Gated> can honor it. */
  defaultAllow: boolean;
};

const CapabilitiesContext = createContext<CapabilitiesApi | null>(null);

export type CapabilitiesProviderProps = {
  clients: ApiClients;
  /** Fail-open while loading? Defaults to true. See file-level comment. */
  defaultAllow?: boolean;
  children: ReactNode;
};

type MyCapabilitiesResponse = {
  role: string;
  normalized_role: string;
  capabilities: string[];
};

export function CapabilitiesProvider({
  clients,
  defaultAllow = true,
  children,
}: CapabilitiesProviderProps) {
  const query = useQuery({
    queryKey: ['capabilities', 'me'],
    queryFn: async (): Promise<MyCapabilitiesResponse> => {
      const { data, error } = await clients.apiGateway.GET(
        '/v1/users/me/capabilities',
      );
      if (error || !data) {
        throw new Error('capabilities fetch failed');
      }
      // The generated type widens role to string; cast through unknown
      // to keep the rest of the consumer code in a narrow shape.
      return data as unknown as MyCapabilitiesResponse;
    },
    // Capabilities change rarely; cache 5 minutes between requests.
    staleTime: 5 * 60 * 1000,
    // Retry once with no backoff — on persistent failure, fail-open.
    retry: 1,
  });

  const api = useMemo<CapabilitiesApi>(() => {
    const caps = new Set<Capability>(
      (query.data?.capabilities ?? []) as Capability[],
    );
    return {
      capabilities: caps,
      loading: query.isLoading,
      role: query.data?.role ?? null,
      normalizedRole: query.data?.normalized_role ?? null,
      has(c) {
        return caps.has(c);
      },
      defaultAllow,
    };
  }, [query.data, query.isLoading, defaultAllow]);

  return (
    <CapabilitiesContext.Provider value={api}>
      {children}
    </CapabilitiesContext.Provider>
  );
}

/**
 * useCapabilities returns the full api. Throws when called outside a
 * provider — same pattern as the rest of the providers in this app.
 */
export function useCapabilities(): CapabilitiesApi {
  const ctx = useContext(CapabilitiesContext);
  if (!ctx) {
    throw new Error('useCapabilities must be used inside <CapabilitiesProvider>');
  }
  return ctx;
}

/**
 * useCapability(name) is a single-purpose accessor — returns true when
 * the bearer has the capability. Honors the loading state per the
 * provider's defaultAllow setting.
 */
export function useCapability(c: Capability): boolean {
  const ctx = useContext(CapabilitiesContext);
  if (!ctx) return true; // no provider → assume open (Storybook isolation)
  if (ctx.loading) return ctx.defaultAllow;
  return ctx.has(c);
}

export type GatedProps = {
  /** Capability the caller must have. */
  capability: Capability;
  /** Rendered when the capability is missing. Defaults to null. */
  fallback?: ReactNode;
  children: ReactNode;
};

/**
 * Gated renders `children` only when the bearer has `capability`. Use
 * for buttons / nav items / sections that shouldn't be visible to
 * roles below the threshold.
 *
 * For more nuanced UX (e.g. show-but-disable + tooltip), call
 * useCapability(...) directly and let the component compose its own
 * shape.
 */
export function Gated({ capability, fallback = null, children }: GatedProps) {
  const allowed = useCapability(capability);
  return <>{allowed ? children : fallback}</>;
}
